/**
 * DEF-FE-GH-002 — groups hub load: summary-first for operators; fleet only on fallback/grantee.
 */

import { pickDevicesPollPath } from "./device-presence.js";
import { fetchFleetDevicesPoll } from "./fleet-list-paginated.js";
import {
  buildGroupsHubSummaryPath,
  buildNotificationGroupSharesFallbackPath,
} from "./groups-hub-api.js";
import { fetchOverviewGroupSettingsList } from "./overview-group-settings-load.js";

/** Admin ``hasOwn`` gate — one cursor page is enough to detect any owned device. */
export const GROUPS_HUB_OWNED_PROBE_MAX_PAGES = 1;

/**
 * @param {string} devPath
 * @param {{ timeoutMs?: number, retries?: number, maxPages?: number }} opts
 */
function fleetPollOpts(devPath, opts = {}) {
  return {
    live: devPath.includes("skip_cache=1"),
    timeoutMs: Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 22000,
    retries: Number.isFinite(Number(opts.retries)) ? Number(opts.retries) : 1,
    maxPages: opts.maxPages,
  };
}

/**
 * Grantee directory — full capped fleet poll (unchanged semantics).
 *
 * @param {(path: string, init?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number, retries?: number, maxPages?: number }} [opts]
 */
export async function fetchGroupsHubGranteeFleet(apiFn, opts = {}) {
  void apiFn;
  const devPath = pickDevicesPollPath();
  const dr = await fetchFleetDevicesPoll(fleetPollOpts(devPath, opts));
  return dr || { items: [] };
}

/**
 * @param {(path: string, init?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number }} [opts]
 * @returns {Promise<boolean>}
 */
export async function probeGroupsHubAdminHasOwnedDevices(apiFn, opts = {}) {
  void apiFn;
  const devPath = pickDevicesPollPath();
  const dr = await fetchFleetDevicesPoll(fleetPollOpts(devPath, {
    ...opts,
    maxPages: GROUPS_HUB_OWNED_PROBE_MAX_PAGES,
  }));
  const items = Array.isArray(dr && dr.items) ? dr.items : [];
  return items.some((d) => d && !d.is_shared);
}

/**
 * @param {(path: string, init?: object) => Promise<unknown>} apiFn
 * @param {{ timeoutMs?: number, retries?: number }} [opts]
 */
export async function fetchGroupsHubSettingsItems(apiFn, opts = {}) {
  try {
    const st = await fetchOverviewGroupSettingsList(apiFn, opts);
    return Array.isArray(st.items) ? st.items : [];
  } catch (_) {
    return [];
  }
}

/**
 * Operator hub — summary first; fleet + NGS only when summary unavailable.
 *
 * @param {(path: string, init?: object) => Promise<unknown>} apiFn
 * @param {{ me: object, saReadonly: boolean, ownerQ: string, seesCustody: boolean }} ctx
 * @param {{ timeoutMs?: number }} [opts]
 */
export async function fetchGroupsHubOperatorBundle(apiFn, ctx, opts = {}) {
  const timeoutMs = Number.isFinite(Number(opts.timeoutMs)) ? Number(opts.timeoutMs) : 22000;
  const { me, saReadonly, ownerQ, seesCustody } = ctx;
  const ownerScope = (me.role === "superadmin" || saReadonly) && ownerQ ? ownerQ : null;

  let settingsItems = [];
  if (seesCustody) {
    settingsItems = await fetchGroupsHubSettingsItems(apiFn, { timeoutMs, retries: 1 });
  }

  let summaryOk = false;
  let summaryItems = [];
  let summaryError = null;
  try {
    const sumR = await apiFn(buildGroupsHubSummaryPath(ownerScope), { timeoutMs });
    summaryItems = Array.isArray(sumR.items) ? sumR.items : [];
    summaryOk = true;
  } catch (e) {
    summaryError = e;
    summaryOk = false;
  }

  let ngItems = [];
  let ngError = null;
  let devices = [];
  let fleetPollList = { items: [] };

  if (!summaryOk) {
    const devPath = pickDevicesPollPath();
    try {
      fleetPollList = await fetchFleetDevicesPoll(fleetPollOpts(devPath, { timeoutMs, retries: 1 }))
        || { items: [] };
      devices = Array.isArray(fleetPollList.items) ? fleetPollList.items : [];
    } catch (_) {
      fleetPollList = { items: [] };
      devices = [];
    }
    try {
      const sr = await apiFn(buildNotificationGroupSharesFallbackPath(ownerScope), { timeoutMs });
      ngItems = Array.isArray(sr.items) ? sr.items : [];
    } catch (e) {
      ngError = e;
      ngItems = [];
    }
  }

  return {
    summaryOk,
    summaryItems,
    summaryError,
    ngItems,
    ngError,
    settingsItems,
    devices,
    fleetPollList,
  };
}
